import type {
  AuthPinCredentials,
  IAuthPinRepository,
} from '@/domain/repositories/IAuthPinRepository';
import { createDeviceSession } from '@/domain/entities/DeviceSession';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { API_BASE_URL } from '@/infrastructure/http/apiConfig';
import { parseJsonResponse } from '@/infrastructure/http/parseJsonResponse';

const AUTH_PIN_URL = `${API_BASE_URL}/events/auth-pin`;

/** PIN inexistente ou evento expirado (HTTP 404/410) — mensagem sempre
 * genérica, o backend nunca revela qual dos dois motivos ocorreu (HUB-21,
 * CA-10, anti-enumeração de PINs). */
export class InvalidPinError extends Error {
  constructor() {
    super('PIN inválido ou evento expirado.');
    this.name = 'InvalidPinError';
  }
}

/**
 * Implementação que autentica um dispositivo via PIN chamando
 * `POST /events/auth-pin` (endpoint público, sem Authorization).
 */
export class HttpAuthPinRepository implements IAuthPinRepository {
  async authenticate({ pin, deviceLabel }: AuthPinCredentials): Promise<DeviceSession> {
    const response = await fetch(AUTH_PIN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin, device_label: deviceLabel }),
    });

    if (response.status === 404 || response.status === 410) {
      throw new InvalidPinError();
    }

    if (!response.ok) {
      throw new Error(`Falha ao validar PIN: HTTP ${response.status}`);
    }

    const data = await parseJsonResponse<unknown>(
      response,
      'Não foi possível validar o PIN. Tente novamente.'
    );
    return createDeviceSession(data);
  }
}
